import * as React from 'react'
import handleViewport from 'react-in-viewport'
import dynamic from 'next/dynamic'
import { css } from 'styled-components'

import {
  BreakpointArray,
  mq,
  spacing,
  styled,
  Text,
  keyframes,
  paddingMixin,
} from '@thg-commerce/gravity-theme'
import { Image, SafeHtml } from '@thg-commerce/gravity-elements'
import {
  CallbackSetEvent,
  PlacementContentType,
  QubitContentType,
  QubitViewType,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Qubit'
import { useLogger, useSiteConfig } from '@thg-commerce/enterprise-core'
import { Orientation } from '@thg-commerce/gravity-patterns/ImageGallery/types'

import { QubitBadge } from '@thg-commerce/enterprise-pages/src/Product/theme'
import { QubitMode } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'

const Close = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Close'),
)

import {
  sendImpression,
  sendPlacementImpressionEvents,
} from '../qubitImpressions'
import { useQubit } from '..'

export type QubitBadgeProps = {
  productId: string
  placementId: string
  type: QubitViewType
  isFullWidth: boolean
  desktopWidth?: string
  setShowBadge?: (value: boolean) => void
  qubitBadgeMessagingStyle?: QubitBadge
}

const BadgeMessageWrapper = styled.div<{
  isParentFullWidth?: boolean
  desktopWidth?: string
  qubitBadgeMessagingStyle?: QubitBadge
}>`
  display: flex;
  position: relative;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.palette.greys.white};
  padding: ${(props) =>
      props?.qubitBadgeMessagingStyle?.padding ? 0 : spacing(0.5)}
    ${spacing(2)};
  opacity: 95%;

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: ${spacing(1)} ${spacing(2)};
    width: ${(props) => (props.isParentFullWidth ? 'auto' : '100%')};
  }

  ${(props) => `width: ${props.desktopWidth}`};
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    ${(props) => `width: ${props.desktopWidth}`};
  }

  ${(props) =>
    props?.qubitBadgeMessagingStyle?.border &&
    `
    ${
      props.qubitBadgeMessagingStyle.border.top.display &&
      `border-top: ${props.qubitBadgeMessagingStyle.border.top.width} solid ${props.qubitBadgeMessagingStyle.border.top.color}`
    };
  ${
    props.qubitBadgeMessagingStyle.border.right.display &&
    ` border-right:${props.qubitBadgeMessagingStyle.border.right} solid ${props.qubitBadgeMessagingStyle.border.right.color}`
  };
  ${
    props.qubitBadgeMessagingStyle.border.left.display &&
    ` border-left:${props.qubitBadgeMessagingStyle.border.left.width} solid ${props.qubitBadgeMessagingStyle.border.left.color}`
  };
  ${
    props.qubitBadgeMessagingStyle.border.bottom.display &&
    ` border-bottom:${props.qubitBadgeMessagingStyle.border.bottom.width} solid ${props.qubitBadgeMessagingStyle.border.bottom.color}`
  };
      border-radius: ${props.qubitBadgeMessagingStyle.border.radius};`}

  ${(props) =>
    props?.qubitBadgeMessagingStyle?.boxShadow &&
    `box-shadow:${props.qubitBadgeMessagingStyle.boxShadow.shadowColor} ${props.qubitBadgeMessagingStyle.boxShadow.shadowY} ${props.qubitBadgeMessagingStyle.boxShadow.shadowX} `}
`

const BadgeMessage = styled(SafeHtml)<{
  qubitBadgeMessagingStyle?: QubitBadge
  showCloseButton?: Boolean
}>`
  margin: 0 ${spacing(1)};
  ${Text('small', 'default')}
  ${(props) => props.showCloseButton && `padding: 0 ${spacing(1)}`};
  ${(props) =>
    props?.qubitBadgeMessagingStyle?.font
      ? `color: ${props.qubitBadgeMessagingStyle.font.textColor};
         strong { color: ${props.qubitBadgeMessagingStyle.font.textColor}};
       ${Text(
         props.qubitBadgeMessagingStyle.font.entry,
         props.qubitBadgeMessagingStyle.font.style,
       )}`
      : ` ${Text('small', 'default')};`}

  ${(props) =>
    props.qubitBadgeMessagingStyle?.padding
      ? paddingMixin(props.qubitBadgeMessagingStyle?.padding)
      : null}
  ${(props) =>
    props.qubitBadgeMessagingStyle?.height
      ? props.qubitBadgeMessagingStyle?.height
      : null}
`

const StyledImage = styled(Image)`
  overflow: unset;
`

const CloseButton = styled.button`
  outline: none;
  border: 2px solid transparent;
  display: block;

  &:hover,
  &:focus {
    opacity: 0.8;
  }

  &:active svg path {
    fill: ${(props) => props.theme.colors.palette.greys.darker};
  }
  &:focus {
    border: 2px solid ${(props) => props.theme.colors.palette.brand.base};
  }
`

const fadeOut = () => keyframes`
  0%, 60% { opacity: 1 }
  70%, 100% { opacity: 0 }
`

export const QubitUrgencyMessagingContainer = styled.div<{
  isGrid: boolean
  orientation: BreakpointArray<Orientation>
  isFullWidth?: boolean
  isFullWidthOnDesktop?: boolean
  horizontalAlignment?: string
  verticalAlignment?: { desktop?: number; mobile?: number }
  showCloseButton?: boolean
  qubitBadgeMessagingStyle?: QubitBadge
}>`
  display: flex;
  justify-content: center;
  height: fit-content;
  top: ${spacing(2)};
  ${(props) => !props.showCloseButton && 'opacity: 0'};
  bottom: ${(props) => (props.isGrid ? `${spacing(17)}` : `${spacing(13)}`)};
  animation: ${(props) =>
    props.showCloseButton ? 'none' : css`10s ${fadeOut()} ease-out`};
  @media (prefers-reduced-motion) {
    animation: none;
  }

  top: ${(props) =>
    props.verticalAlignment?.mobile ||
    props.qubitBadgeMessagingStyle?.container?.verticalAlignmentTop ||
    224}px;

  ${(props) =>
    props?.qubitBadgeMessagingStyle?.container
      ? `transform: ${props.qubitBadgeMessagingStyle.container.transform};
    left: ${props.qubitBadgeMessagingStyle.container.left};
    margin-left : ${props.qubitBadgeMessagingStyle.container.marginLeft.mobile};`
      : `transform: translateY(-50%);`}

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    justify-content: ${(props) => props.horizontalAlignment || 'center'};
    width: ${(props) =>
      props.qubitBadgeMessagingStyle?.container?.width || '48%'};
    padding-left: 2%;
    top: ${(props) =>
      props.verticalAlignment?.desktop ||
      props.qubitBadgeMessagingStyle?.container?.verticalAlignmentTop ||
      190}px;
    bottom: unset;
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    ${(props) =>
      props?.qubitBadgeMessagingStyle?.container
        ? `transform: ${props.qubitBadgeMessagingStyle.container.transform};
            margin-left : ${props.qubitBadgeMessagingStyle.container.marginLeft.desktop}`
        : `transform: translateY(-50%);`}
  }

  position: absolute;
  width: ${(props) =>
    props.qubitBadgeMessagingStyle?.container?.width || '100%'};
  z-index: 5;
`
const BadgeContent = ({
  forwardedRef,
  onClick,
  content,
  isFullWidth,
  desktopWidth,
  setShowBadge,
  showCloseButton,
  qubitBadgeMessagingStyle,
}: {
  forwardedRef: React.RefObject<HTMLDivElement>
  onClick: () => void
  content?: QubitContentType | null
  isFullWidth: boolean
  desktopWidth: string
  setShowBadge: (value: boolean) => void
  showCloseButton: boolean
  qubitBadgeMessagingStyle?: QubitBadge
}) => {
  if (!content) {
    return <div ref={forwardedRef} />
  }

  return (
    <BadgeMessageWrapper
      onClick={onClick}
      ref={forwardedRef}
      isParentFullWidth={isFullWidth}
      desktopWidth={desktopWidth}
      role="button"
      qubitBadgeMessagingStyle={qubitBadgeMessagingStyle}
    >
      {content.imageUrl && (
        <StyledImage
          src={[
            {
              url: content.imageUrl,
            },
          ]}
          alt={content.message || ''}
          isAmp={false}
          width="24px"
          height="24px"
        />
      )}
      {content.message && (
        <BadgeMessage
          qubitBadgeMessagingStyle={qubitBadgeMessagingStyle}
          content={content.message}
          showCloseButton={showCloseButton}
        />
      )}
      {showCloseButton && (
        <CloseButton onClick={() => setShowBadge(false)}>
          <Close fill="#000" />
        </CloseButton>
      )}
    </BadgeMessageWrapper>
  )
}

const BadgeViewport = handleViewport(BadgeContent)

export const QubitUrgencyMessaging = (props: QubitBadgeProps) => {
  const logger = useLogger()
  const { qubit } = useSiteConfig()
  const impressionEmittedRef = React.useRef(false)

  const data = useQubit({
    mode: qubit?.mode || QubitMode.LIVE,
    type: props.type || QubitViewType.PRODUCT,
    placementId: props.placementId,
    productId: props.productId,
  })

  if (!data?.qubitPlacements) {
    return null
  }

  const content =
    data.qubitPlacements.content &&
    data.qubitPlacements.content[PlacementContentType.BADGING]

  const onEnterViewport = () => {
    if (impressionEmittedRef.current) {
      return
    }

    sendPlacementImpressionEvents({
      logger,
      sendPlacementLevelImpression: sendImpression,
      productId: props.productId,
      callbackURL: qubit?.callbackURL,
      callbackData: data.qubitPlacements?.callbackData,
    })

    impressionEmittedRef.current = true
  }

  return (
    <React.Fragment>
      <BadgeViewport
        content={content}
        isFullWidth={props.isFullWidth}
        desktopWidth={props.desktopWidth}
        qubitBadgeMessagingStyle={props.qubitBadgeMessagingStyle}
        onEnterViewport={onEnterViewport}
        onClick={() =>
          sendImpression({
            logger,
            sku: props.productId,
            eventType: CallbackSetEvent.CLICK_THROUGH,
            callbackData: data.qubitPlacements?.callbackData,
            callbackURL: qubit?.callbackURL,
          })
        }
        setShowBadge={props.setShowBadge}
        showCloseButton={qubit?.showUrgencyMsgCloseButton}
      />
    </React.Fragment>
  )
}
